"use client";

import DomPurify from "isomorphic-dompurify";
import { CornerRightDown } from "lucide-react";
import { m } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import useSound from "hooks/useSound";
import { FadeIn } from "components/ui/FadeIn";
import { Button, ButtonVariantTypes, getButtonVariantStylesAndSize } from "components/ui/button";
import MuxPlayer from "components/Tiles/commonComponents/MuxPlayer";
import PlayButton from "./PlayButton";

type ValidTitleTags = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "div" | "span";
type ValidSubHeaderTags = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "div" | "span";
interface ImageVideoProps {
  alt: string;
  src: string;
  title: string;
  subtitle: string;
  audioTrackUrl?: string;
  buttonURL?: string;
  tileUrl?: string; // Keep tileUrl for PlayButton
  titleTag?: ValidTitleTags;
  subHeaderTag?: ValidSubHeaderTags;
  miniTitle?: string;
  body?: string;
  buttonLabel?: string;
  showLearn?: boolean;
  bgColor?: string;
  titleTextColor?: string;
  subtitleTextColor?: string;
  bodyTextColor?: string;
  miniTitleColor?: string;
  glowEffect?: boolean;
  isTop?: boolean;
  fullOverlay?: boolean;
  isTall?: boolean;
  isVideo?: boolean;
  useGifThumbnail?: boolean;
}

const ImageVideo = ({
  alt,
  src,
  title,
  subtitle,
  audioTrackUrl,
  buttonURL,
  tileUrl, // Keep tileUrl
  titleTag = "h1",
  subHeaderTag = "h2",
  miniTitle,
  body,
  buttonLabel,
  showLearn = false,
  bgColor = "",
  titleTextColor = "#ffffff",
  subtitleTextColor = "#ffffff",
  bodyTextColor = "#ffffff",
  miniTitleColor = "#ffffff",
  glowEffect = false,
  isTop = false,
  fullOverlay = false,
  isTall = false,
  isVideo = false,
  useGifThumbnail = false
}: ImageVideoProps) => {
  const TitleComponent = m[titleTag];
  const SubHeaderComponent = m[subHeaderTag];

  const { play, pause, isPlaying } = useSound(audioTrackUrl || "", {
    loop: true
  });

  const togglePlay = () => {
    if (isPlaying) {
      pause();
    } else if (audioTrackUrl) {
      play();
    }
  };

  return (
    <section
      id="ImageVideo-29"
      style={{ background: bgColor }}
      className={`${isTop ? "intro-image -mt-[82px]" : ""} intro-image relative @container`}
    >
      {isTop && <div className={`bnb ${fullOverlay ? "lgs" : ""}`}></div>}
      <div className={`relative ${isTall ? "h-[90vh]" : "h-[750px]"} w-full`}>
        <div className={`relative ${isTall ? "h-[90vh]" : "h-[750px]"}`}>
          {isVideo ? (
            <MuxPlayer playbackId={src} loop muted autoPlay maxResolution="2160p" useGifThumbnail={useGifThumbnail} />
          ) : (
            src && (
              <Image
                src={src}
                alt={alt}
                quality={90}
                fill
                priority
                style={{
                  objectFit: "cover",
                  objectPosition: "50% 62%"
                }}
              />
            )
          )}
          <div className="relative z-20 m-auto flex h-full items-center justify-center px-6 py-20 @2xl:px-28 @2xl:py-12">
            <FadeIn className="mx-auto max-w-[600px] text-center">
              {miniTitle && (
                <p className="mb-4 text-center text-[18px] font-semibold" style={{ color: miniTitleColor }}>
                  {miniTitle}
                </p>
              )}
              <TitleComponent
                className="mb-6 text-center font-heading text-[42px] leading-[48px] [text-wrap:balance] lg:text-[52px] lg:leading-[64px]"
                style={{ color: titleTextColor }}
                dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(title) }}
              />
              <SubHeaderComponent
                className={`${
                  glowEffect ? "smallglow" : ""
                } relative mx-auto mb-12 mt-6 max-w-3xl px-12 text-lg leading-8`}
                style={{ color: subtitleTextColor }}
                dangerouslySetInnerHTML={{ __html: DomPurify.sanitize(subtitle) }}
              />
              {body && (
                <p className="mb-8 text-center text-md [text-wrap:balance] lg:text-lg" style={{ color: bodyTextColor }}>
                  {body}
                </p>
              )}
              {buttonLabel && buttonURL && (
                <Link href={buttonURL} passHref>
                  <button
                    className={getButtonVariantStylesAndSize({ variant: ButtonVariantTypes.BRAND, size: "xl" })}
                    title={buttonLabel}
                  >
                    {buttonLabel}
                  </button>
                </Link>
              )}
              {showLearn && (
                <div className="mt-8 text-center">
                  <Button
                    variant={ButtonVariantTypes.LINK_ICON_ARROW}
                    className="ml-6 items-center justify-center"
                    icon={
                      <CornerRightDown size={20} className="corner-right-down-icon ml-2 mt-[10px]" strokeWidth={2} />
                    }
                  >
                    <Link href="#atom-layout-69"> Or Learn More</Link>
                  </Button>
                </div>
              )}
              {audioTrackUrl && (
                <div className="mb-4 mt-20 flex flex-col items-center justify-center text-xlt font-medium">
                  <PlayButton
                    isPlaying={isPlaying}
                    size={80}
                    iconColor="white"
                    idleBackgroundColor="#000000"
                    activeBackgroundColor="#000000"
                    play={togglePlay}
                    stop={togglePlay}
                    className="fancybutton"
                    tileUrl={tileUrl || ""} // Keep using tileUrl for PlayButton
                  />
                </div>
              )}
            </FadeIn>
          </div>
        </div>
      </div>
      <div className="hero--cover realtive z-0"></div>
    </section>
  );
};

export default ImageVideo;
